import { FC } from 'react';
import { Panel, PanelGroup } from '@cbridge-tools/resizable-panels';
import { useDispatch, useSelector } from '@cbridge-utils/react-redux';
import { RouteSegments } from '@cbridge-packages/routing-client-admin';
import { ConfigPageLayout, SideFilterPanel, usePersistedPanelLayout, panelLayoutSettingKey } from '@cbridge-modules/layout';
import { UserSettingsPage, UserSettingsSection } from '@cbridge-packages/user-settings';
import { SidebarNavigationDrawer } from '@cbridge-modules/sidebar';
import { CoreSymbolGroupsFilter } from '@cbridge-modules/symbol-groups-filter';
import { SymbolGroupsAction, SymbolGroupsSubscriber, selectedSymbolGroupsSelector } from '@cbridge-packages/symbol-groups';
import { ServerListSubscriber } from '@cbridge-packages/server';
import { ClientLpListSubscriber } from '@cbridge-packages/lp-client';
import { useDashboardNavigationItems } from '../../navigation/useDashboardNavigationItems';
import { DashboardsSidebarFooter } from '../../layout/DashboardsSidebarFooter';
import { QuotesViewNavigation } from './navigation/QuotesViewNavigation';

// Mirrors the config-pages layout pattern (ConfigLayout): the dashboards sidebar drawer + a
// ConfigPageLayout split (nav panel + Outlet). Crucially NOT wrapped in MainContainer, whose
// paddingX + extra Toolbar would add a left gap and a doubled top offset over the nav.
// ConfigPageLayout is used with no navMinWidth (like every other config layout): a hard pixel
// minWidth fights react-resizable-panels' percentage sizing and makes the nav panel overlap the page.
export const PricesLayout: FC = () => {
  const dispatch = useDispatch();
  const dashboardsNavigation = useDashboardNavigationItems();
  const selectedSymbolGroups = useSelector(selectedSymbolGroupsSelector);

  const hasActiveFilters = selectedSymbolGroups?.length > 0;
  const handleClearAllFilters = () => dispatch(SymbolGroupsAction.clearSelect());

  const sideLayoutProps = usePersistedPanelLayout(panelLayoutSettingKey(UserSettingsPage.DashboardsPrices, 'side'));

  return (
    <>
      <SidebarNavigationDrawer
        defaultSidebarCollapsed={false}
        isCollapsible={true}
        settingsKey={UserSettingsSection.Dashboards}
        isResizable={true}
        navigation={dashboardsNavigation}
        navigationBasePath={`/${RouteSegments.Root.Dashboard}`}
        slots={{ sidebarFooter: DashboardsSidebarFooter }}
      />
      <ConfigPageLayout settingsKey={UserSettingsPage.DashboardsPrices}>
        <ServerListSubscriber />
        <ClientLpListSubscriber />
        <SymbolGroupsSubscriber />
        <PanelGroup direction={'vertical'} style={{ flex: '1 0 600px' }} {...sideLayoutProps}>
          <Panel id={'list'} minSize={20} defaultSize={60}>
            <QuotesViewNavigation />
          </Panel>
          <SideFilterPanel
            collapsible={true}
            defaultSize={40}
            onClearAllFilters={handleClearAllFilters}
            hasActiveFilters={hasActiveFilters}
          >
            <CoreSymbolGroupsFilter />
          </SideFilterPanel>
        </PanelGroup>
      </ConfigPageLayout>
    </>
  );
};
